<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { X } from 'lucide-vue-next';
import type { ApiImage } from '@/types/images';
import { buildSrcSet, isOptimisedImage } from '@/types/images';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';

export type PhotoCollageItemValue = {
  title?: string | null;
  description?: string | null;
  image?: ApiImage;
};

type CollagePreviewPayload = {
  items?: PhotoCollageItemValue[];
};

const props = withDefaults(
  defineProps<{
    items?: PhotoCollageItemValue[] | null;
    /** Block-picker iframe: JSON from `get_api_representation`. Query key `payload`. */
    payload?: string | null;
    /** First block on page with no PageView hero image: prioritise hero image for LCP. */
    lcpHero?: boolean;
  }>(),
  { items: () => [], lcpHero: false }
);

const previewData = computed((): CollagePreviewPayload | null => {
  const raw = props.payload?.trim();
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as unknown;
    if (p && typeof p === 'object') return p as CollagePreviewPayload;
    return null;
  } catch {
    return null;
  }
});

const selectedIndex = ref<number | null>(null);

const list = computed(() => {
  const raw =
    previewData.value?.items != null ? previewData.value.items : props.items;
  if (!Array.isArray(raw)) return [];
  return raw.filter((it) => it?.image);
});

/** Block-picker iframe: payload set but no image URLs yet (e.g. empty media library). */
const showPreviewPlaceholder = computed(
  () => previewData.value != null && list.value.length === 0
);

const hero = computed(() => list.value[0] ?? null);
const tiles = computed(() => list.value.slice(1, 5));

const selectedItem = computed(() => {
  const i = selectedIndex.value;
  if (i === null || i < 0) return null;
  return list.value[i] ?? null;
});

const modalOpen = computed(() => selectedItem.value != null);

function openAt(index: number) {
  if (index >= 0 && index < list.value.length) {
    selectedIndex.value = index;
  }
}

function closeModal() {
  selectedIndex.value = null;
}

function onEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && modalOpen.value) {
    closeModal();
  }
}

watch(
  () => modalOpen.value,
  (isOpen) => {
    if (typeof window === 'undefined') return;
    if (isOpen) {
      window.addEventListener('keydown', onEscape);
      document.body.style.overflow = 'hidden';
    } else {
      window.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('keydown', onEscape);
  document.body.style.overflow = '';
});

function legacyUrl(img: ApiImage | null | undefined): string | null {
  return img && 'url' in img ? (img.url as string) : null;
}

const heroOptimised = computed(() => {
  const img = hero.value?.image;
  return img != null && isOptimisedImage(img) ? img : null;
});

const selectedOptimised = computed(() => {
  const img = selectedItem.value?.image;
  return img != null && isOptimisedImage(img) ? img : null;
});

const smallGridClass = computed(() => ({
  'photo-collage__small-grid--single': tiles.value.length === 1,
}));
</script>

<template>
  <section
    v-if="showPreviewPlaceholder"
    class="photo-collage photo-collage--preview-placeholder"
    aria-label="Photo collage preview"
  >
    <p class="photo-collage__placeholder-text">
      Upload images to the media library, then add this block and choose your photos.
    </p>
  </section>
  <section v-else-if="hero" class="photo-collage" aria-label="Photo gallery">
    <div
      class="photo-collage__grid"
      :class="{ 'photo-collage__grid--hero-only': tiles.length === 0 }"
    >
      <!-- Hero -->
      <button
        type="button"
        class="photo-collage__hero-btn photo-collage__tile"
        @click="openAt(0)"
      >
        <div class="photo-collage__media">
          <picture v-if="heroOptimised">
            <source
              v-for="source in heroOptimised.sources"
              :key="source.media_type"
              :type="source.media_type"
              :srcset="buildSrcSet(source.src_set)"
            />
            <img
              :src="heroOptimised.fallback!.url"
              :srcset="buildSrcSet(heroOptimised.fallback!.src_set)"
              sizes="(min-width: 1024px) 65vw, 100vw"
              :alt="hero.title ?? ''"
              class="photo-collage__img"
              :loading="lcpHero ? 'eager' : 'lazy'"
              :fetchpriority="lcpHero ? 'high' : undefined"
              decoding="async"
              @error="onOfflineMediaImgError"
            />
          </picture>
          <img
            v-else-if="legacyUrl(hero.image)"
            :src="legacyUrl(hero.image) as string"
            :alt="hero.title ?? ''"
            class="photo-collage__img"
            :loading="lcpHero ? 'eager' : 'lazy'"
            :fetchpriority="lcpHero ? 'high' : undefined"
            decoding="async"
            @error="onOfflineMediaImgError"
          />
          <div class="photo-collage__overlay photo-collage__overlay--hero">
            <p v-if="hero.title" class="photo-collage__title photo-collage__title--hero">
              {{ hero.title }}
            </p>
          </div>
        </div>
      </button>

      <!-- Up to four square tiles (2×2); variants when 1 or 3 tiles -->
      <div v-if="tiles.length" class="photo-collage__small-grid" :class="smallGridClass">
        <button
          v-for="(tile, idx) in tiles"
          :key="idx"
          type="button"
          class="photo-collage__tile-btn photo-collage__tile"
          @click="openAt(idx + 1)"
        >
          <div class="photo-collage__media photo-collage__media--square">
            <picture v-if="tile.image && isOptimisedImage(tile.image)">
              <source
                v-for="source in tile.image.sources"
                :key="source.media_type"
                :type="source.media_type"
                :srcset="buildSrcSet(source.src_set)"
              />
              <img
                :src="tile.image.fallback!.url"
                :srcset="buildSrcSet(tile.image.fallback!.src_set)"
                :alt="tile.title ?? ''"
                class="photo-collage__img"
                loading="lazy"
                @error="onOfflineMediaImgError"
              />
            </picture>
            <img
              v-else-if="tile.image && legacyUrl(tile.image)"
              :src="legacyUrl(tile.image)!"
              :alt="tile.title ?? ''"
              class="photo-collage__img"
              loading="lazy"
              @error="onOfflineMediaImgError"
            />
            <div class="photo-collage__overlay photo-collage__overlay--tile">
              <p v-if="tile.title" class="photo-collage__title photo-collage__title--tile">
                {{ tile.title }}
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="photo-collage-modal">
        <div
          v-if="selectedItem && modalOpen"
          class="photo-collage-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="selectedItem.title ?? 'Image'"
          @click.self="closeModal"
        >
          <div class="photo-collage-modal__card" @click.stop>
            <button
              type="button"
              class="photo-collage-modal__close"
              aria-label="Close modal"
              @click="closeModal"
            >
              <X class="photo-collage-modal__close-icon" aria-hidden="true" />
            </button>
            <div class="photo-collage-modal__figure">
              <picture v-if="selectedOptimised">
                <source
                  v-for="source in selectedOptimised.sources"
                  :key="source.media_type"
                  :type="source.media_type"
                  :srcset="buildSrcSet(source.src_set)"
                />
                <img
                  :src="selectedOptimised.fallback!.url"
                  :srcset="buildSrcSet(selectedOptimised.fallback!.src_set)"
                  :alt="selectedItem.title ?? ''"
                  class="photo-collage-modal__img"
                  @error="onOfflineMediaImgError"
                />
              </picture>
              <img
                v-else-if="selectedItem.image && legacyUrl(selectedItem.image)"
                :src="legacyUrl(selectedItem.image) as string"
                :alt="selectedItem.title ?? ''"
                class="photo-collage-modal__img"
                @error="onOfflineMediaImgError"
              />
            </div>
            <div class="photo-collage-modal__footer">
              <h4 v-if="selectedItem.title" class="photo-collage-modal__heading">
                {{ selectedItem.title }}
              </h4>
              <p v-if="selectedItem.description" class="photo-collage-modal__desc">
                {{ selectedItem.description }}
              </p>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<style scoped>
.photo-collage {
  margin-bottom: 1.5rem;
  /* Contain hover scale / any paint so tiles cannot overlap the partners band or footer */
  overflow: hidden;
  isolation: isolate;
}

.photo-collage--preview-placeholder {
  padding: 1rem 1.25rem;
  border: 1px dashed #d1d5db;
  border-radius: 0.5rem;
  background: #f9fafb;
}

.photo-collage__placeholder-text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #4b5563;
}

.photo-collage__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  min-height: 0;
}

@media (min-width: 768px) {
  .photo-collage__grid {
    grid-template-columns: 1fr 1fr;
    align-items: stretch;
  }

  .photo-collage__grid--hero-only {
    grid-template-columns: 1fr;
  }
}

.photo-collage__hero-btn {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  border-radius: 0.5rem;
  text-align: left;
  overflow: hidden;
}

.photo-collage__hero-btn .photo-collage__media {
  aspect-ratio: 1 / 1;
  height: auto;
}

.photo-collage__small-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  min-height: 0;
}

/* One secondary image: single column (avoid a lone cell in a 2×2). */
.photo-collage__small-grid--single {
  grid-template-columns: 1fr;
}

.photo-collage__tile-btn {
  display: block;
  width: 100%;
  min-height: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  border-radius: 0.5rem;
  text-align: left;
  overflow: hidden;
}

/*
 * Do not set height: 100% on all .photo-collage__media: with square tiles it fights aspect-ratio
 * and can let the image resolve to intrinsic size (huge box, negative layout top, overlap below).
 * Hero column uses height: 100% only on .photo-collage__hero-btn .photo-collage__media.
 */
.photo-collage__media {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 0.5rem;
}

.photo-collage__media--square {
  aspect-ratio: 1 / 1;
  height: auto;
}

.photo-collage__img {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-collage__tile:hover .photo-collage__img {
  transform: scale(1.05);
}

.photo-collage__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  text-align: center;
  background: linear-gradient(
    to top,
    rgb(0 0 0 / 0.6) 0%,
    rgb(0 0 0 / 0) 60%
  );
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.photo-collage__tile:hover .photo-collage__overlay {
  opacity: 1;
}

.photo-collage__overlay--hero {
  padding: 1.5rem;
}

.photo-collage__overlay--tile {
  padding: 0.75rem;
}

.photo-collage__title {
  margin: 0;
  color: #fff;
  font-weight: 600;
}

.photo-collage__title--hero {
  font-size: 1.125rem;
  line-height: 1.4;
}

.photo-collage__title--tile {
  font-size: 0.875rem;
  line-height: 1.4;
}

@media (min-width: 640px) {
  .photo-collage__title--hero {
    font-size: 1.25rem;
  }
}

.photo-collage-modal {
  position: fixed;
  inset: 0;
  z-index: 250;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgb(0 0 0 / 0.8);
}

.photo-collage-modal__card {
  position: relative;
  width: auto;
  max-width: min(80rem, 95vw);
  background: #fff;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

.photo-collage-modal__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  border-radius: 9999px;
  background: rgb(255 255 255 / 0.9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.photo-collage-modal__close:hover {
  background: #fff;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
}

.photo-collage-modal__close-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #111827;
}

.photo-collage-modal__figure {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.photo-collage-modal__img {
  display: block;
  max-width: 100%;
  max-height: 80vh;
  width: auto;
  height: auto;
  object-fit: contain;
}

.photo-collage-modal__footer {
  padding: 1.5rem;
  border-top: 1px solid var(--color-border, #e5e7eb);
}

.photo-collage-modal__heading {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  font-family: var(--font-family-heading, inherit);
  line-height: 1.3;
}

.photo-collage-modal__desc {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: #6b7280;
}

.photo-collage-modal-enter-active,
.photo-collage-modal-leave-active {
  transition: opacity 0.2s ease;
}

.photo-collage-modal-enter-active .photo-collage-modal__card,
.photo-collage-modal-leave-active .photo-collage-modal__card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.photo-collage-modal-enter-from,
.photo-collage-modal-leave-to {
  opacity: 0;
}

.photo-collage-modal-enter-from .photo-collage-modal__card,
.photo-collage-modal-leave-to .photo-collage-modal__card {
  transform: scale(0.96);
  opacity: 0;
}
</style>
